import React, { useCallback, useEffect, useRef, useState } from 'react';

import { DeleteOutline, ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { bulkDeletePromoCodesByIds } from '~/services/PromoService';

import { chunkArray } from '~/utils/chunkArray';

export type BulkOperation = 'delete';
export type BulkOperationRowStatus = 'pending' | 'deleted' | 'failed';
type DeleteScope = 'page' | 'search';
type PromoCodeId = Pick<App.PromoData, 'id_promo_code'>;

interface Props {
  currentPagePromos: Array<PromoCodeId>;
  totalSearchResults: number;
  isUnfilteredAllSearch: boolean;
  /** Fetches one stable page of the current search without changing the visible results. */
  loadSearchPage: (page: number, batchSize: number) => Promise<Array<App.PromoData>>;
  onComplete: () => Promise<void>;
  onOperationChange: (operation: BulkOperation | null) => void;
  onStatusChange: (statuses: Record<string, BulkOperationRowStatus>) => void;
}

type DeleteProgress = {
  deleted: number;
  failed: number;
  processed: number;
  total: number;
  batch: number;
  stopped?: boolean;
};

type DeletePreview = {
  promoCodeIds: Array<string>;
  notFoundPromoCodeIds: Array<string>;
  requested: number;
  batches: number;
};

type BatchLoadProgress = {
  loaded: number;
  total: number;
  page: number;
};

export const PROMO_DELETE_BATCH_SIZE = 100;
export const DEFAULT_PROMO_ID_LOAD_BATCH_SIZE = 500;

export function getSearchPageCount(totalSearchResults: number, pageSize: number) {
  return Math.ceil(totalSearchResults / Math.max(pageSize, 1));
}

export async function loadAllSearchPromoIds(
  totalSearchResults: number,
  batchSize: number,
  loadSearchPage: (page: number, batchSize: number) => Promise<Array<PromoCodeId>>,
  onProgress: (progress: BatchLoadProgress) => void,
  shouldCancel: () => boolean = () => false,
) {
  const promoCodeIds = new Set<string>();
  const pageCount = getSearchPageCount(totalSearchResults, batchSize);

  for (let page = 1; page <= pageCount; page += 1) {
    if (shouldCancel()) {
      return null;
    }
    const promos = await loadSearchPage(page, batchSize);
    if (shouldCancel()) {
      return null;
    }
    promos.forEach((promo) => promoCodeIds.add(promo.id_promo_code));
    onProgress({ loaded: promoCodeIds.size, total: totalSearchResults, page });
  }

  if (promoCodeIds.size !== totalSearchResults) {
    throw new Error(
      `Loaded ${promoCodeIds.size} of ${totalSearchResults} promo codes. Search results changed; refresh the search before loading again.`,
    );
  }

  return Array.from(promoCodeIds);
}

function PromoBulkOperations({
  currentPagePromos,
  totalSearchResults,
  isUnfilteredAllSearch,
  loadSearchPage,
  onComplete,
  onOperationChange,
  onStatusChange,
}: Props) {
  const [isBulkDeleteMode, setBulkDeleteMode] = useState(false);
  const [deleteScope, setDeleteScope] = useState<DeleteScope>('page');
  const [isDeleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const [isLoadingSearchBatch, setLoadingSearchBatch] = useState(false);
  const [loadBatchSize, setLoadBatchSize] = useState(DEFAULT_PROMO_ID_LOAD_BATCH_SIZE);
  const [isSubmitting, setSubmitting] = useState(false);
  const [loadedSearchPromoIds, setLoadedSearchPromoIds] = useState<Array<string> | null>(null);
  const [batchLoadProgress, setBatchLoadProgress] = useState<BatchLoadProgress | null>(null);
  const [preview, setPreview] = useState<DeletePreview | null>(null);
  const [isPreviewing, setPreviewing] = useState(false);
  const [progress, setProgress] = useState<DeleteProgress | null>(null);
  const [error, setError] = useState<string | null>(null);
  const stopRequestedRef = useRef(false);
  const cancelLoadRequestedRef = useRef(false);

  useEffect(() => {
    setLoadedSearchPromoIds(null);
    setBatchLoadProgress(null);
    setPreview(null);
    setError(null);
  }, [loadSearchPage]);

  const setBulkDeleteModeAndReset = (enabled: boolean) => {
    if (enabled && isUnfilteredAllSearch) {
      return;
    }
    setBulkDeleteMode(enabled);
    onOperationChange(enabled ? 'delete' : null);
    onStatusChange({});
    setProgress(null);
    setPreview(null);
    setError(null);
  };

  const changeDeleteScope = (scope: DeleteScope) => {
    setDeleteScope(scope);
    onStatusChange({});
    setProgress(null);
    setPreview(null);
    setError(null);
  };

  const loadSearchBatch = useCallback(async () => {
    cancelLoadRequestedRef.current = false;
    setLoadingSearchBatch(true);
    setLoadedSearchPromoIds(null);
    setBatchLoadProgress(null);
    setPreview(null);
    setError(null);

    try {
      const promoCodeIds = await loadAllSearchPromoIds(
        totalSearchResults,
        loadBatchSize,
        loadSearchPage,
        setBatchLoadProgress,
        () => cancelLoadRequestedRef.current,
      );
      if (promoCodeIds) {
        setLoadedSearchPromoIds(promoCodeIds);
      }
    } catch (loadError) {
      setError((loadError as Error).message || 'Unable to load the matching promo codes.');
    } finally {
      setLoadingSearchBatch(false);
    }
  }, [loadBatchSize, loadSearchPage, totalSearchResults]);

  const cancelSearchBatchLoad = () => {
    cancelLoadRequestedRef.current = true;
    setLoadedSearchPromoIds(null);
    setBatchLoadProgress(null);
  };

  const deletePromoIds = useCallback(
    async (promoCodeIds: Array<string>) => {
      const visiblePromoIds = new Set(currentPagePromos.map((promo) => promo.id_promo_code));
      const visibleIdsInBatch = promoCodeIds.filter((id) => visiblePromoIds.has(id));
      if (visibleIdsInBatch.length > 0) {
        onStatusChange(Object.fromEntries(visibleIdsInBatch.map((id) => [id, 'pending' as const])));
      }

      try {
        const response = await bulkDeletePromoCodesByIds({ promoCodeIds, dryRun: false });
        const deletedIds = new Set(response.result.promoCodeIds);
        if (visibleIdsInBatch.length > 0) {
          onStatusChange(
            Object.fromEntries(visibleIdsInBatch.map((id) => [id, deletedIds.has(id) ? 'deleted' : 'failed'])),
          );
        }

        return { deleted: deletedIds.size, failed: promoCodeIds.length - deletedIds.size };
      } catch (deleteError) {
        if (visibleIdsInBatch.length > 0) {
          onStatusChange(Object.fromEntries(visibleIdsInBatch.map((id) => [id, 'failed' as const])));
        }
        throw deleteError;
      }
    },
    [currentPagePromos, onStatusChange],
  );

  const previewDelete = useCallback(async () => {
    if (isUnfilteredAllSearch || (deleteScope === 'search' && !loadedSearchPromoIds)) {
      return;
    }

    const promoCodeIds =
      deleteScope === 'page' ? currentPagePromos.map((promo) => promo.id_promo_code) : loadedSearchPromoIds;
    setPreviewing(true);
    setPreview(null);
    setError(null);

    try {
      const previewedPromoCodeIds = new Set<string>();
      const notFoundPromoCodeIds = new Set<string>();
      let batches = 0;

      for (const promoCodeIdBatch of chunkArray(promoCodeIds, PROMO_DELETE_BATCH_SIZE)) {
        batches += 1;
        const response = await bulkDeletePromoCodesByIds({ promoCodeIds: promoCodeIdBatch, dryRun: true });
        response.result.promoCodeIds.forEach((id) => previewedPromoCodeIds.add(id));
        response.result.notFoundPromoCodeIds.forEach((id) => notFoundPromoCodeIds.add(id));
      }

      setPreview({
        promoCodeIds: Array.from(previewedPromoCodeIds),
        notFoundPromoCodeIds: Array.from(notFoundPromoCodeIds),
        requested: promoCodeIds.length,
        batches,
      });
    } catch (previewError) {
      setError((previewError as Error).message || 'Unable to review promo code deletion.');
    } finally {
      setPreviewing(false);
    }
  }, [currentPagePromos, deleteScope, isUnfilteredAllSearch, loadedSearchPromoIds]);

  const submitDelete = useCallback(async () => {
    if (!preview || isUnfilteredAllSearch) {
      return;
    }
    setDeleteConfirmationOpen(false);
    setSubmitting(true);
    stopRequestedRef.current = false;
    setError(null);
    setProgress(null);

    try {
      const promoCodeIds = preview.promoCodeIds;
      let batch = 0;
      let processed = 0;
      let deleted = 0;
      let failed = 0;

      for (const promoCodeIdBatch of chunkArray(promoCodeIds, PROMO_DELETE_BATCH_SIZE)) {
        if (stopRequestedRef.current) {
          break;
        }

        batch += 1;
        const result = await deletePromoIds(promoCodeIdBatch);
        processed += promoCodeIdBatch.length;
        deleted += result.deleted;
        failed += result.failed;
        setProgress({
          deleted,
          failed,
          processed,
          total: promoCodeIds.length,
          batch,
          stopped: stopRequestedRef.current,
        });
      }
    } catch (deleteError) {
      setError((deleteError as Error).message || 'Unable to delete promo codes.');
    } finally {
      setSubmitting(false);
      setLoadedSearchPromoIds(null);
      setBatchLoadProgress(null);
      setPreview(null);
      await onComplete();
    }
  }, [deletePromoIds, isUnfilteredAllSearch, onComplete, preview]);

  const stopDelete = () => {
    stopRequestedRef.current = true;
  };

  const selectedCount = deleteScope === 'page' ? currentPagePromos.length : (loadedSearchPromoIds?.length ?? 0);
  const canLoadSearchBatch = totalSearchResults > 0 && !isLoadingSearchBatch && !isSubmitting && !isPreviewing;
  const searchDeleteScopeLabel = loadedSearchPromoIds
    ? `Delete all results (${loadedSearchPromoIds.length})`
    : 'Delete all results (pending load)';

  return (
    <Accordion
      expanded={isBulkDeleteMode}
      disabled={isUnfilteredAllSearch}
      onChange={(_, expanded) => setBulkDeleteModeAndReset(expanded)}
      disableGutters
      sx={{
        border: 1,
        borderColor: 'error.light',
        borderRadius: 1,
        width: isBulkDeleteMode ? '100%' : 'fit-content',
        '&:before': { display: 'none' },
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack direction="row" spacing={1} alignItems="center">
          <DeleteOutline color="error" />
          <Stack>
            <Typography color="error.main" fontWeight="bold">
              Danger Zone
            </Typography>
            {isUnfilteredAllSearch && (
              <Typography variant="caption" color="text.secondary">
                Apply a search filter before enabling deletion
              </Typography>
            )}
          </Stack>
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ borderTop: 1, borderColor: 'error.light', bgcolor: 'background.paper' }}>
        <Stack spacing={2}>
          {isBulkDeleteMode && (
            <>
              <FormControl>
                <FormLabel>Delete scope</FormLabel>
                <RadioGroup row value={deleteScope} onChange={(_, value: DeleteScope) => changeDeleteScope(value)}>
                  <FormControlLabel
                    value="page"
                    control={<Radio />}
                    label={`Delete this page (${currentPagePromos.length})`}
                  />
                  <FormControlLabel value="search" control={<Radio />} label={searchDeleteScopeLabel} />
                </RadioGroup>
              </FormControl>

              {deleteScope === 'search' && (
                <Stack spacing={1} alignItems="flex-start">
                  <TextField
                    label="Load batch size"
                    type="number"
                    value={loadBatchSize}
                    onChange={(event) => setLoadBatchSize(Math.max(1, Number(event.target.value) || 1))}
                    slotProps={{ htmlInput: { min: 1, step: 1 } }}
                    disabled={isLoadingSearchBatch}
                    size="small"
                    sx={{ width: 180 }}
                  />
                  <Stack direction="row" spacing={1}>
                    <Button variant="outlined" color="error" disabled={!canLoadSearchBatch} onClick={loadSearchBatch}>
                      {isLoadingSearchBatch ? 'Loading matching promo IDs...' : 'Load matching promo IDs'}
                    </Button>
                    {isLoadingSearchBatch && (
                      <Button variant="text" color="error" onClick={cancelSearchBatchLoad}>
                        Cancel
                      </Button>
                    )}
                  </Stack>
                  {batchLoadProgress && (
                    <Typography variant="body2">
                      Loaded {batchLoadProgress.loaded} of {batchLoadProgress.total} promo IDs from page{' '}
                      {batchLoadProgress.page}.
                    </Typography>
                  )}
                  {loadedSearchPromoIds && (
                    <Alert severity="info">
                      Loaded {loadedSearchPromoIds.length} promo IDs. You can now review this deletion.
                    </Alert>
                  )}
                </Stack>
              )}

              <Alert severity="warning">
                {deleteScope === 'page'
                  ? `Review the ${selectedCount} promo code${selectedCount === 1 ? '' : 's'} displayed on the current page before permanently deleting them.`
                  : `Review the ${selectedCount} loaded promo code${selectedCount === 1 ? '' : 's'} before permanently deleting them. The delete uses that fixed result set, so pagination changes cannot skip records.`}
              </Alert>

              {preview && (
                <Alert severity={preview.promoCodeIds.length > 0 ? 'info' : 'warning'}>
                  Review complete: {preview.promoCodeIds.length} of {preview.requested} promo code
                  {preview.requested === 1 ? '' : 's'} will be deleted in {preview.batches} request
                  {preview.batches === 1 ? '' : 's'}.
                  {preview.notFoundPromoCodeIds.length > 0
                    ? ` ${preview.notFoundPromoCodeIds.length} could not be found and will not be deleted.`
                    : ''}
                </Alert>
              )}

              {progress && (
                <Alert severity={progress.failed > 0 ? 'warning' : 'success'}>
                  {progress.stopped ? 'Delete stopped. ' : ''}Processed {progress.processed} of {progress.total} promo
                  codes in {progress.batch} request
                  {progress.batch === 1 ? '' : 's'}: {progress.deleted} deleted
                  {progress.failed > 0 ? `, ${progress.failed} failed` : ''}.
                </Alert>
              )}

              {error && <Alert severity="error">{error}</Alert>}

              <Stack direction="row" spacing={1}>
                <Button
                  variant="outlined"
                  color="error"
                  disabled={selectedCount === 0 || isSubmitting || isLoadingSearchBatch || isPreviewing}
                  onClick={previewDelete}
                >
                  {isPreviewing
                    ? 'Reviewing deletion...'
                    : `Review ${deleteScope === 'page' ? 'this deletion' : 'all results'}`}
                </Button>
                <Button
                  color="error"
                  variant="contained"
                  startIcon={<DeleteOutline />}
                  disabled={!preview || preview.promoCodeIds.length === 0 || isSubmitting || isPreviewing}
                  onClick={() => setDeleteConfirmationOpen(true)}
                >
                  {isSubmitting ? 'Deleting promo codes...' : `Delete ${preview?.promoCodeIds.length ?? 0} promo codes`}
                </Button>
                {isSubmitting && (
                  <Button color="error" variant="outlined" onClick={stopDelete}>
                    Stop delete
                  </Button>
                )}
              </Stack>
            </>
          )}
        </Stack>
      </AccordionDetails>

      <Dialog open={isDeleteConfirmationOpen} onClose={() => setDeleteConfirmationOpen(false)}>
        <DialogTitle>Review deletion</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This review found {preview?.promoCodeIds.length ?? 0} promo code
            {preview?.promoCodeIds.length === 1 ? '' : 's'} that will be permanently deleted
            {deleteScope === 'search' ? ' from all loaded results' : ' from the current page'}. This action cannot be
            undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmationOpen(false)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={submitDelete}>
            Delete promo codes
          </Button>
        </DialogActions>
      </Dialog>
    </Accordion>
  );
}

export default PromoBulkOperations;
